import { html, unsafeCSS } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { PopBaseComponent } from '../../base/pop-base-component';

import style from './key-fact.scss?inline';
import { Constants } from './key-fact.constants';
import { IconSVG } from '../../common/utils/icon';
import { dateShort } from '../../common/utils/formatDate';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { formatKeyFactValue } from './key-fact.utils';
import { ValueFormat } from './key-fact.model';
import { MorningstarMedalistRating } from '../../common/enums/morningstar-enum';
import { sentenceToKebabCase } from '../../common/utils/toKebabCase';
import { ensureParagraphTag } from '../../common/utils/textFormat';

/**
 * @tag pop-key-fact-component
 */
@customElement(Constants.TAG_NAME)
export class PopKeyFactComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * Component style variant
   * @type string
   */
  @property({ attribute: Constants.DATA_VARIANT, type: String })
  variant = Constants.SUMMARY_KEY_FACTS_BLOCK;

  /**
   * Title of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_TITLE, type: String })
  title;

  /**
   * Content of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_CONTENT, type: String })
  content?: string;

  /**
   * Sub-content of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_SUB_CONTENT, type: String })
  subContent?: string;

  /**
   * Sub-title of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_SUBTITLE, type: String })
  subtitle?: string;

  /**
   * Fact tooltip content
   * @type string
   */
  @property({ attribute: Constants.DATA_TOOLTIP, type: String })
  tooltip?: string;

  /**
   * Date of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_AS_OF_DATE, type: String })
  asOfDate?: string;

  /**
   * Value format of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_VALUE_FORMAT, type: String })
  valueFormat?: string;

  /**
   * Disclaimer code of the fact
   * @type string
   */
  @property({ attribute: Constants.DATA_DISCLAIMER_CODE, type: String })
  disclaimerCode?: string;

  constructor() {
    super();
  }

  renderTooltip() {
    if (this.tooltip) {
      const tooltipID = `${this.variant}-${sentenceToKebabCase(this.title)}`;
      const tooltipTriggerLabel = `${this.getTitle()} details`;

      return html`
        <beacon-icon-button
          id="${tooltipID}"
          class="tooltip-icon-button"
          variant="tertiary"
        >
          ${tooltipTriggerLabel}
          <beacon-icon slot="icon" name="information"></beacon-icon>
        </beacon-icon-button>
        <beacon-popover opens-by="#${tooltipID}">
          ${unsafeHTML(sanitizeHTML(ensureParagraphTag(this.tooltip)))}
        </beacon-popover>
      `;
    }

    return html``;
  }

  renderSubContent() {
    if (this.subContent) {
      return html`<div class="key-fact-sub-content">${this.subContent}</div>`;
    }

    return html``;
  }

  renderContent() {
    if (this.content) {
      const formattedKeyFactValue = formatKeyFactValue(
        this.content,
        this.valueFormat,
      );
      const isLongContent = this.content.length >= 20;
      const isStarsContent = this.valueFormat === ValueFormat.stars;
      const isMedalistContent =
        this.valueFormat === ValueFormat.morningstarMedalistRating;

      let className = 'key-fact-content';
      let screenReaderText;

      if (isLongContent) {
        className += '-long';
      } else if (isStarsContent) {
        className += '-stars';
        screenReaderText = `${this.content} stars`;
      } else if (isMedalistContent) {
        className += '-medalist';
      }

      if (isStarsContent) {
        return html`
          <div class="${className}">
            <div role="img" aria-label="${screenReaderText}">
              <span aria-hidden="true">${formattedKeyFactValue}</span>
            </div>
          </div>
          ${this.renderSubContent()}
        `;
      } else if (isMedalistContent) {
        return html`
          <div class="${className}">
            ${IconSVG.morningstarMedalistRating(
              MorningstarMedalistRating[this.content.toUpperCase()],
              { ariaLabel: this.content },
            )}
          </div>
          ${this.renderSubContent()}
        `;
      } else {
        return html`
          <div class="${className}">${formattedKeyFactValue}</div>
          ${this.renderSubContent()}
        `;
      }
    }

    return html`
      <div class="key-fact-content">${Constants.NOT_APPLICABLE}</div>
    `;
  }

  renderSubtitle() {
    if (this.subtitle) {
      return html`<div class="key-fact-subtitle">${this.subtitle}</div>`;
    }

    return html``;
  }

  renderAsOfDate() {
    if (this.asOfDate) {
      const text = `as of ${dateShort(this.asOfDate)}`;
      return html`<div class="key-fact-as-of-date">${text}</div>`;
    }

    return html``;
  }

  getTitle() {
    return this.title;
  }

  render() {
    return this.renderComponent(html`
      <div class="key-fact-container ${this.variant}">
        <div class="key-fact-title">
          ${this.renderTooltip()}
          ${this.disclaimerCode
            ? html`<span>${this.getTitle()}</span
                ><span data-disclaimer-code="${this.disclaimerCode}"></span>`
            : html`<span>${this.getTitle()}</span>`}
        </div>
        <div class="key-fact-content-container">${this.renderContent()}</div>
        ${this.renderSubtitle()} ${this.renderAsOfDate()}
      </div>
    `);
  }
}
